import React, { useState, useEffect } from 'react';
import { useAdmin } from '../components/AdminContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import BlogManager from '../components/BlogManager';
import JobManager from '../components/JobManager';
import TestimonialsManager from '../components/TestimonialsManager';
import PageEditManager from '../components/PageEditManager';

const AdminDashboard = ({ activeTab: propActiveTab }) => {
  const { adminLogout } = useAdmin();
  const navigate = useNavigate();
  const { pageName } = useParams();
  const location = useLocation();
  const [editingPage, setEditingPage] = useState(null);
  
  // Determine active tab from props or URL
  const activeTab = propActiveTab || 'edit-pages';
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [employeeApplications, setEmployeeApplications] = useState([]);
  const [hiringRequests, setHiringRequests] = useState([]);
  const [newsletterSubscriptions, setNewsletterSubscriptions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [downloadingCvId, setDownloadingCvId] = useState(null);

  const handleLogout = () => {
    adminLogout();
  };

  // Fetch employee applications
  const fetchEmployeeApplications = async () => {
    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('employee_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmployeeApplications(data || []);
    } catch (error) {
      console.error('Error fetching employee applications:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // Fetch hiring requests
  const fetchHiringRequests = async () => {
    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('hiring_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHiringRequests(data || []);
    } catch (error) {
      console.error('Error fetching hiring requests:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // Fetch newsletter subscriptions
  const fetchNewsletterSubscriptions = async () => {
    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNewsletterSubscriptions(data || []);
    } catch (error) {
      console.error('Error fetching newsletter subscriptions:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // Download CV file
  const downloadCV = async (cvFileUrl, cvFileName, applicationId) => {
    try {
      setDownloadingCvId(applicationId || null);
      const { data, error } = await supabase.storage
        .from('cv-files')
        .download(cvFileUrl);

      if (error) throw error;

      // Create blob URL and trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = cvFileName || 'cv.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CV:', error);
      alert('Error downloading CV file');
    } finally {
      setDownloadingCvId(null);
    }
  };

  // Update application status
  const updateApplicationStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('employee_applications')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      fetchEmployeeApplications(); // Refresh data
    } catch (error) {
      console.error('Error updating application status:', error);
    }
  };

  // Update hiring request status
  const updateHiringStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('hiring_requests')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      fetchHiringRequests(); // Refresh data
    } catch (error) {
      console.error('Error updating hiring request status:', error);
    }
  };

  // Update newsletter subscription status
  const updateNewsletterStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      fetchNewsletterSubscriptions(); // Refresh data
    } catch (error) {
      console.error('Error updating newsletter subscription status:', error);
    }
  };

  // Get status color for hiring requests
  const getHiringStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      reviewed: 'bg-blue-100 text-blue-800 border-blue-200',
      contacted: 'bg-purple-100 text-purple-800 border-purple-200',
      in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get status color for employee applications
  const getApplicationStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      reviewed: 'bg-blue-100 text-blue-800 border-blue-200',
      contacted: 'bg-purple-100 text-purple-800 border-purple-200',
      hired: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Get status color for newsletter subscriptions
  const getNewsletterStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      unsubscribed: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Filter and sort employee applications
  const getFilteredAndSortedApplications = () => {
    let filtered = employeeApplications;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Filter and sort hiring requests
  const getFilteredAndSortedRequests = () => {
    let filtered = hiringRequests;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(req => 
        req.person_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Filter and sort newsletter subscriptions
  const getFilteredAndSortedSubscriptions = () => {
    let filtered = newsletterSubscriptions;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.source && sub.source.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => sub.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'employee-applications') {
      fetchEmployeeApplications();
    } else if (activeTab === 'hiring-requests') {
      fetchHiringRequests();
    } else if (activeTab === 'newsletter-subscriptions') {
      fetchNewsletterSubscriptions();
    }
  }, [activeTab]);

  const handleEditPage = (pageName) => {
    navigate(`/admin/edit-pages/${pageName}`);
  };

  // Set editing page from URL params
  useEffect(() => {
    if (activeTab === 'edit-pages' && pageName) {
      setEditingPage(pageName);
    } else {
      setEditingPage(null);
    }
  }, [activeTab, pageName]);

  const handleSaveContent = async (content) => {
    setLoading(true);
    try {
      console.log('Attempting to save content:', { pageName: editingPage, content });
      
      // Use the correct table name
      const { data, error } = await supabase
        .from('page_contents')
        .upsert({
          page_name: editingPage,
          content: content,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'page_name'
        })
        .select();

      console.log('Supabase response:', { data, error });

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      
      console.log('Content saved successfully:', data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving content:', error);
      console.error('Error details:', error.message, error.details, error.hint);
      
      if (error.message.includes('401') || error.message.includes('JWT')) {
        alert('Authentication error: Please check your Supabase RLS policies. You may need to disable RLS for the page-contents table or set up proper policies.');
      } else if (error.message.includes('relation') || error.message.includes('does not exist')) {
        alert('Table not found: Please ensure the "page-contents" or "page_contents" table exists in your Supabase database.');
      } else {
        alert(`Error saving content: ${error.message}. Please check the console for details.`);
      }
    }
    setLoading(false);
  };

  const TabButton = ({ id, label, icon, path }) => (
    <button
      onClick={() => navigate(path)}
      className={`w-full flex items-center px-6 py-4 text-sm font-medium transition-colors border-b border-gray-200 ${
        activeTab === id
          ? 'bg-brand-blue text-white'
          : 'text-black hover:bg-gray-50'
      }`}
    >
      <span className="mr-3">{icon}</span>
      {label}
    </button>
  );

  const renderContent = () => {
    if (editingPage) {
      return <PageEditManager pageName={editingPage} onSave={handleSaveContent} />;
    }

    switch (activeTab) {
      case 'blogs':
        return <BlogManager />;

      case 'employee-applications':
        const filteredApplications = getFilteredAndSortedApplications();
        return (
          <div className="bg-white shadow-sm">
            {/* Search and Filter Bar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by name, email, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="contacted">Contacted</option>
                    <option value="hired">Hired</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="created_at-desc">Newest First</option>
                    <option value="created_at-asc">Oldest First</option>
                    <option value="full_name-asc">Name A-Z</option>
                    <option value="full_name-desc">Name Z-A</option>
                    <option value="status-asc">Status A-Z</option>
                  </select>
                  <button
                    onClick={() => window.open('/find-jobs?redirect=/admin/employee-applications', '_blank')}
                    className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Add New</span>
                  </button>
                  <button
                    onClick={fetchEmployeeApplications}
                    disabled={loadingData}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Name</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Email</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Location</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">CV</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Status</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Applied</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-8 px-6 text-center text-gray-500">
                        {loadingData ? 'Loading applications...' : searchTerm || filterStatus !== 'all' ? 'No applications match your search criteria' : 'No applications found'}
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map((application) => (
                      <tr key={application.id} className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                        <td className="py-4 px-6 text-black font-medium text-sm">{application.full_name}</td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{application.email}</td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{application.location}</td>
                        <td className="py-4 px-6">
                          {application.cv_file_url ? (
                            <button
                              onClick={() => downloadCV(application.cv_file_url, application.cv_file_name, application.id)}
                              disabled={downloadingCvId === application.id}
                              className={`text-sm font-medium flex items-center gap-2 ${downloadingCvId === application.id ? 'text-gray-400 cursor-not-allowed' : 'text-brand-blue hover:text-blue-700'}`}
                            >
                              {downloadingCvId === application.id ? (
                                <>
                                  <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                  </svg>
                                  <span>Downloading...</span>
                                </>
                              ) : (
                                <span>Download CV</span>
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">No CV</span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <select
                            value={application.status}
                            onChange={(e) => updateApplicationStatus(application.id, e.target.value)}
                            className={`text-xs px-3 py-2 pr-8 rounded-full border font-medium focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none cursor-pointer ${getApplicationStatusColor(application.status)}`}
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1rem 1rem'
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="contacted">Contacted</option>
                            <option value="hired">Hired</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">
                          {new Date(application.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6">
                          <button
                            onClick={() => window.open(`/employee-application/${application.id}`, '_blank')}
                            className="text-brand-blue hover:text-blue-700 px-3 py-1 rounded text-sm font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'hiring-requests':
        const filteredRequests = getFilteredAndSortedRequests();
        return (
          <div className="bg-white shadow-sm">
            {/* Search and Filter Bar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by name, company, email, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="contacted">Contacted</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="created_at-desc">Newest First</option>
                    <option value="created_at-asc">Oldest First</option>
                    <option value="person_name-asc">Name A-Z</option>
                    <option value="person_name-desc">Name Z-A</option>
                    <option value="business_name-asc">Company A-Z</option>
                    <option value="status-asc">Status A-Z</option>
                  </select>
                  <button
                    onClick={() => window.open('/hire-talent?redirect=/admin/hiring-requests', '_blank')}
                    className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Add New</span>
                  </button>
                  <button
                    onClick={fetchHiringRequests}
                    disabled={loadingData}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Contact</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Company</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Title</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Location</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Status</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Submitted</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-8 px-6 text-center text-gray-500">
                        {loadingData ? 'Loading requests...' : searchTerm || filterStatus !== 'all' ? 'No requests match your search criteria' : 'No hiring requests found'}
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                        <td className="py-4 px-6">
                          <div>
                            <div className="text-black font-medium text-sm">{request.person_name}</div>
                            <div className="text-gray-600 text-xs">{request.email}</div>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{request.business_name}</td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{request.title}</td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{request.location}</td>
                        <td className="py-4 px-6">
                          <select
                            value={request.status}
                            onChange={(e) => updateHiringStatus(request.id, e.target.value)}
                            className={`text-xs px-3 py-2 pr-8 rounded-full border font-medium focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none cursor-pointer ${getHiringStatusColor(request.status)}`}
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1rem 1rem'
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="contacted">Contacted</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">
                          {new Date(request.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6">
                          <button
                            onClick={() => window.open(`/hiring/${request.id}`, '_blank')}
                            className="text-brand-blue hover:text-blue-700 px-3 py-1 rounded text-sm font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'newsletter-subscriptions':
        const filteredSubscriptions = getFilteredAndSortedSubscriptions();
        return (
          <div className="bg-white shadow-sm">
            {/* Search and Filter Bar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by email or source..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                  >
                    <option value="created_at-desc">Newest First</option>
                    <option value="created_at-asc">Oldest First</option>
                    <option value="email-asc">Email A-Z</option>
                    <option value="email-desc">Email Z-A</option>
                    <option value="status-asc">Status A-Z</option>
                  </select>
                  <button
                    onClick={fetchNewsletterSubscriptions}
                    disabled={loadingData}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Email</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Source</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Status</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Subscribed</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-8 px-6 text-center text-gray-500">
                        {loadingData ? 'Loading subscriptions...' : searchTerm || filterStatus !== 'all' ? 'No subscriptions match your search criteria' : 'No newsletter subscriptions found'}
                      </td>
                    </tr>
                  ) : (
                    filteredSubscriptions.map((subscription) => (
                      <tr key={subscription.id} className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                        <td className="py-4 px-6 text-black font-medium text-sm">{subscription.email}</td>
                        <td className="py-4 px-6 text-gray-600 text-sm">{subscription.source || 'website'}</td>
                        <td className="py-4 px-6">
                          <select
                            value={subscription.status}
                            onChange={(e) => updateNewsletterStatus(subscription.id, e.target.value)}
                            className={`text-xs px-3 py-2 pr-8 rounded-full border font-medium focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none cursor-pointer ${getNewsletterStatusColor(subscription.status)}`}
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1rem 1rem'
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="unsubscribed">Unsubscribed</option>
                          </select>
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">
                          {new Date(subscription.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">
                          {subscription.updated_at ? new Date(subscription.updated_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'job-board':
        return <JobManager />;
      
      case 'testimonials':
        return <TestimonialsManager />;
      
      case 'edit-pages':
        return (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm first:rounded-tl-xl">Page</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Path</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Status</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm">Last Modified</th>
                    <th className="text-left py-4 px-6 text-black font-medium text-sm last:rounded-tr-xl">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-5 px-6 text-black font-medium text-sm">Home</td>
                    <td className="py-5 px-6 text-gray-600 text-sm">/</td>
                    <td className="py-5 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                        Published
                      </span>
                    </td>
                    <td className="py-5 px-6 text-gray-600 text-sm">2 days ago</td>
                    <td className="py-5 px-6">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPage('home')}
                          className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => window.open('/', '_blank')}
                          className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                    <td className="py-5 px-6 text-black font-medium text-sm">Specialisms</td>
                    <td className="py-5 px-6 text-gray-600 text-sm">/specialisms</td>
                    <td className="py-5 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                        Published
                      </span>
                    </td>
                    <td className="py-5 px-6 text-gray-600 text-sm">1 week ago</td>
                    <td className="py-5 px-6">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPage('specialisms')}
                          className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => window.open('/specialisms', '_blank')}
                          className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                    <td className="py-5 px-6 text-black font-medium text-sm">Employers</td>
                    <td className="py-5 px-6 text-gray-600 text-sm">/employers</td>
                    <td className="py-5 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                        Published
                      </span>
                    </td>
                    <td className="py-5 px-6 text-gray-600 text-sm">5 days ago</td>
                    <td className="py-5 px-6">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPage('employers')}
                          className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => window.open('/employers', '_blank')}
                          className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                    <td className="py-5 px-6 text-black font-medium text-sm">Candidates</td>
                    <td className="py-5 px-6 text-gray-600 text-sm">/candidates</td>
                    <td className="py-5 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                        Published
                      </span>
                    </td>
                    <td className="py-5 px-6 text-gray-600 text-sm">1 week ago</td>
                    <td className="py-5 px-6">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPage('candidates')}
                          className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => window.open('/candidates', '_blank')}
                          className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors border-t border-gray-100">
                    <td className="py-5 px-6 text-black font-medium text-sm">About Us</td>
                    <td className="py-5 px-6 text-gray-600 text-sm">/about</td>
                    <td className="py-5 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                        Published
                      </span>
                    </td>
                    <td className="py-5 px-6 text-gray-600 text-sm">2 weeks ago</td>
                    <td className="py-5 px-6">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditPage('about')}
                          className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => window.open('/about', '_blank')}
                          className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="bg-white flex items-center justify-center min-h-full">
            <div className="max-w-2xl mx-auto text-center">
              <img 
                src="/building.jpg" 
                alt="Under Construction" 
                className="w-full h-auto rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-black mb-2">Coming Soon</h3>
              <p className="text-black">This section is currently under development.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="h-screen bg-white flex">
      {/* Left Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
        {/* Logo Section */}
        <div className="px-6 py-3 border-b border-gray-200 h-[57px] flex items-center flex-shrink-0">
          <img 
            src="/jcr_logo.jpg" 
            alt="JCR Logo" 
            className="h-8 w-auto"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto">
          <TabButton
            id="edit-pages"
            label="Edit Pages"
            path="/admin/edit-pages"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            }
          />
          <TabButton
            id="blogs"
            label="Blogs"
            path="/admin/blogs"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
          <TabButton
            id="employee-applications"
            label="Employee Applications"
            path="/admin/employee-applications"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          />
          <TabButton
            id="hiring-requests"
            label="Hiring Requests"
            path="/admin/hiring-requests"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          />
          <TabButton
            id="job-board"
            label="Job Board"
            path="/admin/job-board"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.25 14.15v4.25c0 .414-.336.75-.75.75h-4.5a.75.75 0 01-.75-.75v-4.25m0 0h4.5m-4.5 0l-1.5 1.5m1.5-1.5l-1.5 1.5M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
              </svg>
            }
          />
          <TabButton
            id="testimonials"
            label="Testimonials"
            path="/admin/testimonials"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
          />
          <TabButton
            id="newsletter-subscriptions"
            label="Newsletter Subscriptions"
            path="/admin/newsletter-subscriptions"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
        </nav>
      </aside>

      {/* Right Section */}
      <main className="flex-1 flex flex-col bg-white">
        {/* Top Bar with User Info & Logout */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 h-[57px] flex items-center flex-shrink-0">
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center space-x-4">
              {editingPage && (
                <button
                  onClick={() => navigate('/admin/edit-pages')}
                  className="text-black hover:bg-gray-100 p-2 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h2 className="text-xl font-bold text-black">
                {editingPage ? `Edit ${editingPage} Page` : (activeTab === 'edit-pages' ? 'Edit Pages' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1))}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {editingPage ? (
                <>
                  {saved && <span className="text-green-600 text-sm">Saved!</span>}
                  <button
                    onClick={() => {
                      // Trigger save from the PageEditManager component
                      const saveEvent = new CustomEvent('triggerSave');
                      window.dispatchEvent(saveEvent);
                    }}
                    disabled={loading}
                    className="bg-brand-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors text-sm"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-right">
                    <p className="text-sm font-medium text-black">James Carpenter</p>
                    <p className="text-xs text-black">Administrator</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="bg-brand-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={activeTab === 'blogs' ? 'px-8 py-6' : ''}>
            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard; 